import { toast } from '@cotera/client/app/components/ui/toaster';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import type { QueryClient } from '@tanstack/react-query';
import { ok } from 'neverthrow';
import React from 'react';
import { z } from 'zod/v3';
import {
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../actions/framework';
import { GRAPH_ACTION_GROUP } from '../dataset/actions/constants';
import { GraphModel } from '../graph/graph.model';
import { NewToolModal } from './new-tool-modal';
import { ToolModel } from './tool.model';

type Params = {};

export class NewToolAction extends BaseAction<Params> {
  title = 'New Tool';
  description = 'Opens a new tool editor';
  inputSchema = z.object({});
  group = GRAPH_ACTION_GROUP;
  icon = 'wrench-screwdriver';

  constructor(
    private readonly modalManager: {
      push: (opts: {
        view: () => React.ReactNode;
        size?: 'auto' | 'lg';
      }) => void;
      close: () => void;
    },
    private readonly navigator: {
      push: (opts: {
        route: string;
        title?: string;
        params: Record<string, string | undefined>;
      }) => void;
    },
    private readonly queryClient: QueryClient,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.isInScope(GraphModel);
  }

  async execute(_: z.infer<typeof this.inputSchema>, context: ExecuteContext) {
    const graphModel = context.getInScopeModelOfType(GraphModel);

    if (graphModel === null) {
      return ok({});
    }

    const saveToolToGraph = async (tool: {
      id: string;
      name: string;
      toolConnectionId: string;
    }): Promise<boolean> => {
      // Create a ToolModel with the tool's metadata
      const toolModel = new ToolModel(
        {
          name: tool.name,
          graph: graphModel.graph,
          compiler: graphModel.compiler,
        },
        { isNew: true }
      );

      // Set the tool metadata
      toolModel.toolMetadata.set({
        toolId: tool.id,
        toolConnectionId: tool.toolConnectionId,
      });

      // Compile to generate graph operations
      const result = toolModel.compile({ setOperations: true });

      if (result.isErr()) {
        toast.error(`Failed to compile tool: ${JSON.stringify(result.error)}`);
        return false;
      }

      const operations = toolModel.newOperations.snapshot();
      if (operations.length === 0) {
        return true;
      }

      // Stage operations to the graph
      const stagingResult = await graphModel.attemptToStageOperations(
        operations
      );

      if (stagingResult.isErr()) {
        toast.error(
          `Failed to save tool: ${JSON.stringify(stagingResult.error)}`
        );
        return false;
      }

      // Invalidate queries to refetch tools
      await this.queryClient.invalidateQueries({
        queryKey: queryKeys.tools.all({ orgId: this.orgId }),
      });
      await this.queryClient.invalidateQueries({
        queryKey: queryKeys.connections.all({ orgId: this.orgId }),
      });

      return true;
    };

    this.modalManager.push({
      view: () => (
        <NewToolModal
          onComplete={async (tool) => {
            // Save new tool to graph - modal will show success screen
            await saveToolToGraph({
              id: tool.id,
              name: tool.name,
              toolConnectionId: tool.toolConnectionId,
            });
          }}
          onClose={() => this.modalManager.close()}
        />
      ),
      size: 'lg',
    });

    return ok({});
  }
}
